import { Loader2, PlayCircle, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchBacktestResult, fetchBacktestStatus, runBacktest,
  fetchStrategyResult, fetchStrategyStatus, runStrategyBacktest,
} from '../api';
import { BacktestResult, StrategyResult } from '../types';
import GuideCard from './GuideCard';

// 🎨 규격 V2(08-23) 공용 부품 — 버튼은 고스트/켜짐 두 가지뿐(파란 채움 버튼 은퇴), 색은 dc 토큰만.
const BTN = 'inline-flex items-center gap-1.5 h-[26px] px-2 sm:h-[29px] sm:px-[11px] rounded-lg border text-[11.5px] sm:text-xs transition-colors';
const GHOST = `${BTN} border-[#202A3C] text-dc-ink2/90 font-medium hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-40 disabled:hover:border-[#202A3C]`;
const ON = `${BTN} border-dc-point/35 bg-dc-point/10 text-dc-point font-semibold disabled:opacity-40`;
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';

// 차트 색 — 토큰 hex만(상승 웜 / 하락 쿨 / 강조 앰버 / 선·글자는 잉크 램프).
const C = {
  up: '#E06C5B', down: '#5C8FD6', point: '#DCA85E',
  grid: '#141B28', axis: '#5E6B82', tick2: '#9DA9BE',
  panel: '#101623', line1: '#1C2534', ink1: '#E9EFF8',
};
const TOOLTIP_STYLE = { background: C.panel, border: `1px solid ${C.line1}`, borderRadius: 8, color: C.ink1, fontSize: 11.5 };

// 점수 구간 사다리 — 높을수록 밝게, 최상단만 앰버(초록·노랑·보라 은퇴).
const BAR_COLORS = ['#5E6B82', '#5E6B82', '#9DA9BE', '#E9EFF8', '#DCA85E'];

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="border-t border-dc-line1 pt-2.5 flex flex-col gap-1">
      <span className={KICKER}>{label}</span>
      <span className={`text-[17.5px] font-[750] tabular-nums ${accent ?? 'text-dc-ink1'}`}>{value}</span>
      {sub && <span className="text-dc-ink3 text-[11.5px]">{sub}</span>}
    </div>
  );
}

type Mode = 'score' | 'strategy';

export default function BacktestPanel() {
  const [mode, setMode] = useState<Mode>('strategy');
  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
      <div className="-mx-6 -mt-2 mb-3">
        <GuideCard id="backtest" title="백테스트 사용법">
          <p className="mb-1.5"><b className="text-dc-ink1">이 화면은?</b> 이 앱의 점수·전략이 <b>과거에 실제로 통했는지</b> 데이터로 검증해요.</p>
          <p className="mb-1.5"><b className="text-dc-ink2">이렇게 쓰세요:</b> ① 모드(점수 구간 / 전략) 선택 → ② 실행 → ③ 점수 구간별 <b>평균 수익률·승률</b> 확인(점수 높을수록 잘 나오는지).</p>
          <p className="text-dc-ink3"><b>⚠️ 한계:</b> 상장폐지 종목 제외(생존편향)·거래비용 가정 등이 있어 '참고용'이에요. 미래 수익 보장이 아닙니다.</p>
        </GuideCard>
      </div>
      {/* 모드 토글 */}
      <div className="flex gap-1.5 mb-5">
        <button
          onClick={() => setMode('strategy')}
          className={mode === 'strategy' ? ON : GHOST}
        >
          전략 검증 (손익)
        </button>
        <button
          onClick={() => setMode('score')}
          className={mode === 'score' ? ON : GHOST}
        >
          점수 검증
        </button>
      </div>

      {mode === 'strategy' ? <StrategyBacktest /> : <ScoreBacktest />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 전략 백테스트 — 진입 + 손절/청산 규칙으로 실제 매매 손익 검증
// ════════════════════════════════════════════════════════════════
function StrategyBacktest() {
  const [result, setResult]   = useState<StrategyResult | null>(null);
  const [running, setRunning] = useState(false);
  const [runTime, setRunTime] = useState<string | null>(null);
  const [error, setError]     = useState('');
  const [mode, setMode]         = useState<'trend' | 'reversion'>('trend');
  const [scoreMin, setScoreMin] = useState(80);
  const [revMin, setRevMin]     = useState(65);
  const [stopPct, setStopPct]   = useState(7);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const isRev = mode === 'reversion';

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const loadResult = useCallback(async () => {
    try { setResult(await fetchStrategyResult()); } catch { /* none */ }
  }, []);

  const startPolling = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const s = await fetchStrategyStatus();
        if (!s.running) { stopPoll(); setRunning(false); if (s.has_result) await loadResult(); }
      } catch { /* silent */ }
    }, 3000);
  }, [loadResult]);

  useEffect(() => {
    fetchStrategyStatus().then((s) => {
      setRunning(s.running);
      setRunTime(s.run_time);
      if (s.has_result) loadResult();
      if (s.running) startPolling();
    }).catch(() => {});
    return stopPoll;
  }, [loadResult, startPolling]);

  const handleRun = async () => {
    setError('');
    setRunning(true);
    setResult(null);
    try {
      await runStrategyBacktest(
        isRev
          ? { mode: 'reversion', rev_min: revMin, stop_pct: stopPct, max_hold: 20 }
          : { mode: 'trend', score_min: scoreMin, stop_pct: stopPct }
      );
      startPolling();
    } catch {
      setError('전략 백테스트 실행 실패');
      setRunning(false);
    }
  };

  const formattedTime = runTime
    ? new Date(runTime).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    : null;

  const positive = result ? result.expectancy > 0 : false;

  return (
    <>
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-dc-ink1 text-[14px] font-[750]">전략 백테스트</h2>
          <p className="text-dc-ink3 text-[11.5px] mt-0.5">
            {isRev
              ? `역추세 점수≥${revMin} 진입 → 손절 −${stopPct}% / MA20 복귀 시 익절. 반등 매매 손익 검증.`
              : `점수≥${scoreMin} 진입 → 손절 −${stopPct}% / MA20 추세이탈 청산. 추세 매매 손익 검증.`}
          </p>
          {/* 모드 토글 */}
          <div className="flex gap-1.5 mt-2 w-fit">
            {([['trend', '추세'], ['reversion', '역추세']] as const).map(([k, label]) => (
              <button key={k} onClick={() => setMode(k)} className={mode === k ? ON : GHOST}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-2">
          <label className={`flex flex-col gap-1 ${KICKER}`}>
            {isRev ? '역추세 점수' : '진입 점수'}
            <input type="number" min={0} max={100} value={isRev ? revMin : scoreMin}
              onChange={(e) => (isRev ? setRevMin : setScoreMin)(Number(e.target.value))}
              className="w-16 bg-dc-bg1 border border-[#202A3C] rounded-lg h-[29px] px-2 text-xs tracking-normal text-dc-ink2 tabular-nums outline-none focus:border-dc-point/50" />
          </label>
          <label className={`flex flex-col gap-1 ${KICKER}`}>
            손절 %
            <input type="number" min={1} max={30} value={stopPct}
              onChange={(e) => setStopPct(Number(e.target.value))}
              className="w-16 bg-dc-bg1 border border-[#202A3C] rounded-lg h-[29px] px-2 text-xs tracking-normal text-dc-ink2 tabular-nums outline-none focus:border-dc-point/50" />
          </label>
          <button
            onClick={handleRun}
            disabled={running}
            className={ON}
          >
            {running
              ? <><Loader2 size={14} className="animate-spin" /> 실행 중...</>
              : result
              ? <><RefreshCw size={14} /> 재실행</>
              : <><PlayCircle size={14} /> 실행</>}
          </button>
        </div>
      </div>

      {error && <p className="text-dc-warn text-[13px] mb-4">{error}</p>}

      {running && (
        <div className="flex items-center gap-3 py-10 justify-center text-dc-ink3 text-[13px]">
          <Loader2 size={18} className="animate-spin text-dc-point" />
          <span>매매 시뮬레이션 중... (1~2분 소요)</span>
        </div>
      )}

      {!running && !result && (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <PlayCircle size={40} className="text-dc-line2" />
          <p className="text-dc-ink3 text-[13px]">실행을 눌러 "이 점수로 진입하고 규칙대로 청산하면 돈을 버는가"를 검증하세요.</p>
        </div>
      )}

      {result?.error && !running && (
        <p className="text-dc-warn text-[13px] py-10 text-center">{result.error}</p>
      )}

      {result && !result.error && !running && (
        <>
          {/* 핵심 지표: 거래당 기대값 */}
          <div className={`rounded-lg px-4 py-4 mb-4 border ${
            positive ? 'border-dc-up/25 bg-dc-up/[0.04]' : 'border-dc-down/25 bg-dc-down/[0.04]'
          }`}>
            <p className={`${KICKER} mb-1`}>거래당 기대값 (Expectancy)</p>
            <p className={`text-[27px] font-[760] tracking-[-.015em] tabular-nums ${positive ? 'text-dc-up' : 'text-dc-down'}`}>
              {result.expectancy >= 0 ? '+' : ''}{result.expectancy}%
            </p>
            <p className="text-dc-ink3 text-[11.5px] mt-1">
              한 번 매매할 때 평균적으로 기대되는 수익률. {positive ? '양수 = 통계적 우위 존재.' : '음수 = 이 규칙은 손실.'}
            </p>
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4 mb-5">
            <StatCard label="승률" value={`${result.win_rate}%`}
              sub={`총 ${result.total_trades.toLocaleString('ko-KR')}거래`} />
            <StatCard label="손익비 (Payoff)" value={`${result.payoff_ratio}`}
              sub="평균이익 / 평균손실" accent={result.payoff_ratio >= 1.5 ? 'text-dc-ok' : 'text-dc-ink1'} />
            <StatCard label="평균 이익" value={`+${result.avg_win}%`} accent="text-dc-up"
              sub={`평균 손실 ${result.avg_loss}%`} />
            <StatCard label="평균 보유" value={`${result.avg_hold_days}일`}
              sub={`최고 +${result.best_trade}% / 최악 ${result.worst_trade}%`} />
          </div>

          {/* 자본 곡선 */}
          <div className="border-b border-dc-hair pb-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <p className={KICKER}>누적 자본 곡선</p>
              <div className="flex gap-3 text-[11.5px]">
                <span className="text-dc-ink3">누적 <span className={`tabular-nums ${result.cumulative_return >= 0 ? 'text-dc-up' : 'text-dc-down'}`}>
                  {result.cumulative_return >= 0 ? '+' : ''}{result.cumulative_return}%</span></span>
                <span className="text-dc-ink3">MDD <span className="tabular-nums text-dc-down">{result.max_drawdown}%</span></span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={result.equity_curve} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.point} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={C.point} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                <XAxis dataKey="i" tick={{ fill: C.axis, fontSize: 10.5 }} />
                <YAxis tick={{ fill: C.axis, fontSize: 10.5 }} tickFormatter={(v) => `${v}x`} domain={['auto', 'auto']} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v) => [`${Number(v).toFixed(3)}x`, '자본']}
                  labelFormatter={(l) => `${l}번째 거래`}
                />
                <Area type="monotone" dataKey="equity" stroke={C.point} strokeWidth={1.5} fill="url(#eq)" />
              </AreaChart>
            </ResponsiveContainer>
            <p className="text-dc-ink3/70 text-[10.5px] mt-1">
              ※ 거래당 자본 {Math.round(result.position_fraction * 100)}% 배분(분산) 가정. 청산일 순 복리.
            </p>
          </div>

          {/* 청산 사유 */}
          <div className="border-b border-dc-hair pb-4 mb-4">
            <p className={`${KICKER} mb-3`}>청산 사유 분포</p>
            <ResponsiveContainer width="100%" height={120}>
              <ComposedChart data={result.exit_reasons} layout="vertical" margin={{ top: 0, right: 30, left: 20, bottom: 0 }}>
                <XAxis type="number" tick={{ fill: C.axis, fontSize: 10.5 }} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="reason" tick={{ fill: C.tick2, fontSize: 11.5 }} width={70} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v, _n, item) => [`${item.payload.count}건 (${v}%)`, item.payload.reason]}
                />
                <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
                  {result.exit_reasons.map((e, i) => (
                    <Cell key={i} fill={e.reason === '손절' ? C.down : e.reason === '익절' ? C.up : C.axis} />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {result.point_in_time && (
            <div className="flex justify-center">
              <span
                className="inline-flex items-center gap-1 h-[22px] px-2.5 rounded-full border border-dc-ok/35 bg-dc-ok/[0.06] text-dc-ok text-[11.5px] font-medium"
                title="각 시점의 진입 판정은 그날까지의 데이터만 사용합니다(미래정보 차단). 단, 체결가는 당일 종가/손절가 가정이라 완벽한 실거래 재현은 아닙니다."
              >
                🔒 미래정보 차단 — 각 시점은 그날까지 데이터만 사용
              </span>
            </div>
          )}
          {result.limitations && result.limitations.length > 0 && (
            <div className="rounded-lg border border-dc-warn/30 bg-dc-warn/[0.05] px-3 py-2">
              <p className="text-dc-warn text-[11.5px] font-semibold mb-1">⚠️ 백테스트 한계 — 이 숫자를 과신하지 마세요</p>
              <ul className="space-y-0.5">
                {result.limitations.map((l, i) => (
                  <li key={i} className="text-[#E0B27C]/85 text-[11.5px] leading-snug flex gap-1.5">
                    <span className="text-dc-warn/70 shrink-0">·</span><span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-dc-ink3/70 text-[11.5px] text-center">
            {formattedTime} · {result.ticker_count}개 종목
          </p>
        </>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// 점수 백테스트 — 점수 구간별 예측력 검증 (기존)
// ════════════════════════════════════════════════════════════════
function ScoreBacktest() {
  const [result, setResult]     = useState<BacktestResult | null>(null);
  const [running, setRunning]   = useState(false);
  const [runTime, setRunTime]   = useState<string | null>(null);
  const [error, setError]       = useState('');
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const loadResult = useCallback(async () => {
    try { setResult(await fetchBacktestResult()); } catch { /* no result yet */ }
  }, []);

  const startPolling = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const s = await fetchBacktestStatus();
        if (!s.running) { stopPoll(); setRunning(false); if (s.has_result) await loadResult(); }
      } catch { /* silent */ }
    }, 3000);
  }, [loadResult]);

  useEffect(() => {
    fetchBacktestStatus().then((s) => {
      setRunning(s.running);
      setRunTime(s.run_time);
      if (s.has_result) loadResult();
      if (s.running) startPolling();
    }).catch(() => {});
    return stopPoll;
  }, [loadResult, startPolling]);

  const handleRun = async () => {
    setError('');
    setRunning(true);
    try {
      await runBacktest();
      startPolling();
    } catch {
      setError('백테스트 실행 실패');
      setRunning(false);
    }
  };

  const chartData = result?.buckets.map((b, i) => ({
    name:       b.range,
    avg_return: b.avg_return,
    win_rate:   b.win_rate,
    count:      b.count,
    color:      BAR_COLORS[i] ?? '#5E6B82',
  })) ?? [];

  const formattedTime = runTime
    ? new Date(runTime).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    : null;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-dc-ink1 text-[14px] font-[750]">점수 백테스트</h2>
          <p className="text-dc-ink3 text-[11.5px] mt-0.5">
            점수 구간별 20거래일 후 평균 수익률 · 승률 검증 (+2% 초과 승리)
          </p>
        </div>
        <button
          onClick={handleRun}
          disabled={running}
          className={ON}
        >
          {running
            ? <><Loader2 size={14} className="animate-spin" /> 실행 중...</>
            : result
            ? <><RefreshCw size={14} /> 재실행</>
            : <><PlayCircle size={14} /> 백테스트 실행</>}
        </button>
      </div>

      {error && <p className="text-dc-warn text-[13px] mb-4">{error}</p>}

      {running && (
        <div className="flex items-center gap-3 py-10 justify-center text-dc-ink3 text-[13px]">
          <Loader2 size={18} className="animate-spin text-dc-point" />
          <span>200개 종목 × 400일 데이터 분석 중... (1~2분 소요)</span>
        </div>
      )}

      {!running && !result && (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <PlayCircle size={40} className="text-dc-line2" />
          <p className="text-dc-ink3 text-[13px]">백테스트 실행 버튼을 눌러 점수의 실제 예측력을 검증하세요.</p>
          <p className="text-dc-ink3/70 text-[11.5px]">200개 종목, 약 1~2년치 데이터 기준</p>
        </div>
      )}

      {result && !running && (
        <>
          <div className="grid grid-cols-3 gap-x-4 gap-y-4 mb-6">
            <StatCard label="전체 샘플" value={result.total_samples.toLocaleString('ko-KR')} sub={`${result.ticker_count}개 종목`} />
            <StatCard label="전체 승률" value={`${result.overall_win_rate}%`} sub={`${result.forward_days}거래일 기준`} />
            <StatCard label="분석 기준일" value={formattedTime?.split(' ')[0] ?? '─'} sub={formattedTime?.split(' ')[1] ?? ''} />
          </div>

          <div className="border-b border-dc-hair pb-4 mb-4">
            <p className={`${KICKER} mb-3`}>점수 구간별 평균 수익률 (20거래일 후)</p>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                <XAxis dataKey="name" tick={{ fill: C.axis, fontSize: 11.5 }} />
                <YAxis yAxisId="left" tick={{ fill: C.axis, fontSize: 11.5 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value, name) => {
                    const v = Number(value);
                    return [
                      name === 'avg_return' ? `${v.toFixed(2)}%` : `${v.toFixed(1)}%`,
                      name === 'avg_return' ? '평균 수익률' : '승률',
                    ] as [string, string];
                  }}
                />
                <Legend formatter={(v) => v === 'avg_return' ? '평균 수익률' : '승률'} wrapperStyle={{ fontSize: 11.5, color: C.tick2 }} />
                <Bar yAxisId="left" dataKey="avg_return" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.avg_return >= 0 ? C.up : C.down} />
                  ))}
                </Bar>
                <Line yAxisId="left" type="monotone" dataKey="win_rate" stroke={C.point} strokeWidth={2} dot={{ fill: C.point, r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[11.5px] tabular-nums">
              <thead>
                <tr className="border-b border-dc-hair">
                  <th className={`px-3 py-2.5 text-left ${KICKER}`}>점수 구간</th>
                  <th className={`px-3 py-2.5 text-right ${KICKER}`}>평균 수익률</th>
                  <th className={`px-3 py-2.5 text-right ${KICKER}`}>승률</th>
                  <th className={`px-3 py-2.5 text-right ${KICKER}`}>샘플 수</th>
                </tr>
              </thead>
              <tbody>
                {result.buckets.map((b) => (
                  <tr key={b.range} className="border-b border-dc-hair/80 hover:bg-white/[0.015] transition-colors">
                    <td className="px-3 py-2.5 text-dc-ink2">{b.range}</td>
                    <td className={`px-3 py-2.5 text-right font-semibold ${b.avg_return >= 0 ? 'text-dc-up' : 'text-dc-down'}`}>
                      {b.avg_return >= 0 ? '+' : ''}{b.avg_return.toFixed(2)}%
                    </td>
                    <td className="px-3 py-2.5 text-right text-dc-ink2">{b.win_rate.toFixed(1)}%</td>
                    <td className="px-3 py-2.5 text-right text-dc-ink3">{b.count.toLocaleString('ko-KR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.point_in_time && (
            <div className="flex justify-center mt-3">
              <span
                className="inline-flex items-center gap-1 h-[22px] px-2.5 rounded-full border border-dc-ok/35 bg-dc-ok/[0.06] text-dc-ok text-[11.5px] font-medium"
                title="각 시점의 점수는 그날까지의 데이터만으로 계산합니다(미래정보 차단)."
              >
                🔒 미래정보 차단 — 각 시점 점수는 그날까지 데이터만 사용
              </span>
            </div>
          )}
          {result.limitations && result.limitations.length > 0 && (
            <div className="rounded-lg border border-dc-warn/30 bg-dc-warn/[0.05] px-3 py-2 mt-3">
              <p className="text-dc-warn text-[11.5px] font-semibold mb-1">⚠️ 백테스트 한계 — 이 숫자를 과신하지 마세요</p>
              <ul className="space-y-0.5">
                {result.limitations.map((l, i) => (
                  <li key={i} className="text-[#E0B27C]/85 text-[11.5px] leading-snug flex gap-1.5">
                    <span className="text-dc-warn/70 shrink-0">·</span><span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </>
  );
}
